/**
 * Style hints captured from a Word paragraph before a full-text replace,
 * then re-applied to matching substrings in the new text.
 */

export type ParagraphStyleHint = {
  readonly text: string;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
};

const MIN_HINT_TEXT = 3;

/**
 * Keep hints that carry local emphasis and still appear in the replacement text.
 * Longer hints first so re-apply prefers more specific matches.
 */
export const styleHintsToReapply = (
  hints: readonly ParagraphStyleHint[],
  newText: string,
): ParagraphStyleHint[] => {
  const haystack = newText;
  const filtered = hints.filter((h) => {
    const t = h.text.trim();
    if (t.length < MIN_HINT_TEXT) {
      return false;
    }
    if (!h.bold && !h.italic && !h.underline) {
      return false;
    }
    return haystack.includes(t);
  });
  // Dedupe by text (keep first / longest preference after sort).
  filtered.sort((a, b) => b.text.length - a.text.length);
  const seen = new Set<string>();
  const out: ParagraphStyleHint[] = [];
  for (const h of filtered) {
    const key = h.text;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(h);
  }
  return out;
};

/**
 * Collapse adjacent same-style tokens into phrase hints when possible.
 */
export const mergeAdjacentStyleTokens = (
  tokens: readonly ParagraphStyleHint[],
): ParagraphStyleHint[] => {
  if (tokens.length === 0) {
    return [];
  }
  const merged: ParagraphStyleHint[] = [];
  let cur = { ...tokens[0]! };
  for (let i = 1; i < tokens.length; i += 1) {
    const next = tokens[i]!;
    const same =
      cur.bold === next.bold &&
      cur.italic === next.italic &&
      cur.underline === next.underline;
    if (same && (cur.bold || cur.italic || cur.underline)) {
      cur = {
        text: `${cur.text} ${next.text}`.replace(/\s+/g, " ").trim(),
        bold: cur.bold,
        italic: cur.italic,
        underline: cur.underline,
      };
    } else {
      if (cur.bold || cur.italic || cur.underline) {
        merged.push(cur);
      }
      cur = { ...next };
    }
  }
  if (cur.bold || cur.italic || cur.underline) {
    merged.push(cur);
  }
  return merged;
};
